import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Alert, Autocomplete, Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import api from '../services/api';

function TipoBeneficioLista() {
    const navigate = useNavigate();
    const [tipoBeneficioList, setTipoBeneficioList] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ valor: '', categoria: '', ativo: true, ordem: 0 });
    const [error, setError] = useState('');

    useEffect(() => {
        loadTipoBeneficioList();
        loadCategorias();
    }, []);

    const loadTipoBeneficioList = async () => {
        setLoading(true);
        try {
            const response = await api.get('/tipo-beneficio-lista');
            setTipoBeneficioList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de tipos de benefício:', error);
            setError('Erro ao carregar lista de tipos de benefício');
        } finally {
            setLoading(false);
        }
    };

    const loadCategorias = async () => {
        try {
            const response = await api.get('/tipo-beneficio-lista/categorias');
            setCategorias(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar categorias:', error);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ 
                valor: item.valor, 
                categoria: item.categoria || '', 
                ativo: item.ativo === 1, 
                ordem: item.ordem || 0 
            });
        } else {
            setEditingItem(null);
            setFormData({ valor: '', categoria: '', ativo: true, ordem: 0 });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ valor: '', categoria: '', ativo: true, ordem: 0 });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.valor.trim()) {
            setError('Valor é obrigatório');
            return;
        }

        try {
            if (editingItem) {
                await api.put(`/tipo-beneficio-lista/${editingItem.id}`, formData);
            } else {
                await api.post('/tipo-beneficio-lista', formData);
            }
            handleCloseDialog();
            loadTipoBeneficioList();
            loadCategorias();
        } catch (error) {
            console.error('Erro ao salvar tipo de benefício:', error);
            setError(error.response?.data?.error || 'Erro ao salvar tipo de benefício');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este tipo de benefício?')) {
            try {
                await api.delete(`/tipo-beneficio-lista/${id}`);
                loadTipoBeneficioList();
            } catch (error) {
                console.error('Erro ao deletar tipo de benefício:', error);
                alert('Erro ao deletar tipo de benefício: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const tipoBeneficioFiltrados = tipoBeneficioList.filter(t => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            t.valor.toLowerCase().includes(searchLower) ||
            (t.categoria && t.categoria.toLowerCase().includes(searchLower))
        );
    });

    // Agrupar por categoria para exibição
    const grouped = tipoBeneficioFiltrados.reduce((acc, item) => {
        const cat = item.categoria || 'Sem categoria';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {});

    return (
        <Container>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">
                    Gerenciar Tipos de Benefício
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Novo Tipo de Benefício
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por valor ou categoria..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell align="center">Ativo</TableCell>
                            <TableCell align="center">Ordem</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : tipoBeneficioFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {search ? 'Nenhum tipo de benefício encontrado' : 'Nenhum tipo de benefício cadastrado'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            tipoBeneficioFiltrados.map((tipo) => (
                                <TableRow key={tipo.id} hover>
                                    <TableCell>
                                        {tipo.categoria ? (
                                            <Chip label={tipo.categoria} size="small" variant="outlined" />
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell>{tipo.valor}</TableCell>
                                    <TableCell align="center">
                                        {tipo.ativo === 1 ? '✓' : '✗'}
                                    </TableCell>
                                    <TableCell align="center">{tipo.ordem || 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(tipo)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(tipo.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Tipo de Benefício' : 'Novo Tipo de Benefício'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Valor"
                            value={formData.valor}
                            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                            margin="normal"
                            required
                        />
                        <Autocomplete
                            freeSolo
                            options={categorias}
                            value={formData.categoria}
                            onChange={(e, newValue) => {
                                setFormData({ ...formData, categoria: newValue || '' });
                            }}
                            onInputChange={(e, newInputValue) => {
                                setFormData({ ...formData, categoria: newInputValue });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Categoria"
                                    margin="normal"
                                />
                            )}
                        />
                        <TextField
                            fullWidth
                            label="Ordem"
                            type="number"
                            value={formData.ordem}
                            onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.ativo}
                                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                />
                            }
                            label="Ativo"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default TipoBeneficioLista;

